/**
 * Data-validation rules as the Data Validation Rules Manager edits them (Excel's
 * "Data Validation" dialog, per rule): the cells, what is allowed, the input message and
 * the error alert. The workbook's own rules are loaded as-is; `toDvEdits` turns the edited
 * list back into the minimal server edits, identifying a file rule by its ORIGINAL
 * position in the worksheet's `<dataValidations>` list. Pure functions — unit-tested.
 *
 * Values travel in Excel's stored form (no leading '='): an inline list is quoted
 * ("Absent,Present"), a list from cells is a reference ($AI$2:$AI$5), a date is its
 * serial number. The dialog edits friendlier forms; the helpers below convert.
 */
import type { DataValidationEdit } from './sheetEdits';
import type { WorkbookDvRule } from './templateWorkbook';

export const DV_TYPES: { id: string; label: string }[] = [
  { id: 'list', label: 'List' },
  { id: 'whole', label: 'Whole number' },
  { id: 'decimal', label: 'Decimal' },
  { id: 'date', label: 'Date' },
  { id: 'textLength', label: 'Text length' },
  { id: 'custom', label: 'Custom (formula)' },
];
/** Rule types the dialog creates and edits; others (time, any) can be deleted, not edited. */
export const EDITABLE_DV_TYPES = new Set(DV_TYPES.map(t => t.id));

export const DV_OPERATORS: { id: string; label: string }[] = [
  { id: 'between', label: 'between' },
  { id: 'notBetween', label: 'not between' },
  { id: 'equal', label: 'equal to' },
  { id: 'notEqual', label: 'not equal to' },
  { id: 'greaterThan', label: 'greater than' },
  { id: 'lessThan', label: 'less than' },
  { id: 'greaterThanOrEqual', label: 'greater than or equal to' },
  { id: 'lessThanOrEqual', label: 'less than or equal to' },
];
const OP_LABEL: Record<string, string> = Object.fromEntries(DV_OPERATORS.map(o => [o.id, o.label]));
const TYPE_LABEL: Record<string, string> = { ...Object.fromEntries(DV_TYPES.map(t => [t.id, t.label])), time: 'Time', any: 'Any value' };

export const DV_ERROR_STYLES: { id: string; label: string }[] = [
  { id: 'stop', label: 'Stop — reject the value' },
  { id: 'warning', label: 'Warning — ask to continue' },
  { id: 'information', label: 'Information — accept with a note' },
];

/** The part of a rule the New / Edit Rule dialog edits. */
export interface DvDefinition {
  range: string;
  type: string;
  operator: string;
  formula1: string | null;
  formula2: string | null;
  allowBlank: boolean;
  inCellDropdown: boolean;
  showInputMessage: boolean;
  inputTitle: string | null;
  inputMessage: string | null;
  showErrorMessage: boolean;
  errorStyle: string;
  errorTitle: string | null;
  errorMessage: string | null;
}

export interface DvRule extends DvDefinition {
  /** stable key while the manager is open */
  key: string;
  sheetIndex: number;
  /** the rule's position in the FILE; null = added in this session */
  origIndex: number | null;
}

export const EMPTY_DV: DvDefinition = {
  range: '', type: 'list', operator: 'between', formula1: null, formula2: null,
  allowBlank: true, inCellDropdown: true, showInputMessage: false, inputTitle: null, inputMessage: null,
  showErrorMessage: true, errorStyle: 'stop', errorTitle: null, errorMessage: null,
};

let seq = 0;

export function dvRulesFromWorkbook(rules: WorkbookDvRule[] | undefined): DvRule[] {
  return [...(rules ?? [])]
    .sort((a, b) => a.sheetIndex - b.sheetIndex || a.index - b.index)
    .map(r => ({
      key: `f${r.sheetIndex}-${r.index}`, sheetIndex: r.sheetIndex, origIndex: r.index,
      range: r.range, type: r.type, operator: r.operator, formula1: r.formula1, formula2: r.formula2,
      allowBlank: r.allowBlank, inCellDropdown: r.inCellDropdown,
      showInputMessage: r.showInputMessage, inputTitle: r.inputTitle, inputMessage: r.inputMessage,
      showErrorMessage: r.showErrorMessage, errorStyle: r.errorStyle, errorTitle: r.errorTitle, errorMessage: r.errorMessage,
    }));
}

export function newDvRule(sheetIndex: number, def: DvDefinition): DvRule {
  return { ...def, key: `n${++seq}`, sheetIndex, origIndex: null };
}

// ── list source ↔ Excel formula1 ──────────────────────────────────────────────

/** Excel formula1 → what the Source box shows: `Absent, Present` or `=$AI$2:$AI$5`. */
export function listSourceText(formula1: string | null): string {
  const f = (formula1 ?? '').trim();
  if (f.startsWith('"') && f.endsWith('"') && f.length >= 2)
    return f.slice(1, -1).replace(/""/g, '"').split(',').map(s => s.trim()).join(', ');
  return f ? `=${f.replace(/^=/, '')}` : '';
}

/** The Source box → Excel formula1: `=…` is a cell reference, anything else an inline list. Null when empty. */
export function listFormula(source: string): string | null {
  const s = source.trim();
  if (!s) return null;
  if (s.startsWith('=')) return s.slice(1).trim() || null;
  const items = s.split(',').map(x => x.trim()).filter(Boolean);
  return items.length ? `"${items.join(',').replace(/"/g, '""')}"` : null;
}

/** The values an inline list offers (empty for a cell-reference list). */
export function inlineListValues(formula1: string | null): string[] {
  const f = (formula1 ?? '').trim();
  if (!(f.startsWith('"') && f.endsWith('"'))) return [];
  return f.slice(1, -1).replace(/""/g, '"').split(',').map(s => s.trim()).filter(Boolean);
}

// ── dates ↔ Excel serial numbers ──────────────────────────────────────────────

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const DAY = 86_400_000;

/** `2026-10-12` (date input) → `46307` (Excel serial); null when not a date. */
export function dateToSerial(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  return String(Math.round((Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!) - EXCEL_EPOCH) / DAY));
}

/** `46307` → `2026-10-12` for the date input; null when the value is a formula / not a serial. */
export function serialToDate(serial: string | null): string | null {
  const s = (serial ?? '').trim();
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const d = new Date(EXCEL_EPOCH + Math.floor(Number(s)) * DAY);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** A date value for the rule TEXT — MM/DD/YYYY (the app-wide date format), else the formula as stored. */
function dateText(v: string | null): string {
  const iso = serialToDate(v);
  if (!iso) return v ?? '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

// ── description ──────────────────────────────────────────────────────────────

/** Rule text: `List: Absent, Present`, `Decimal between 0 and 100`, `Text length ≤ 20`, … */
export function describeDv(r: Pick<DvDefinition, 'type' | 'operator' | 'formula1' | 'formula2'>): string {
  const label = TYPE_LABEL[r.type] ?? r.type;
  if (r.type === 'list') return `List: ${listSourceText(r.formula1)}`;
  if (r.type === 'custom') return `Custom: =${(r.formula1 ?? '').replace(/^=/, '')}`;
  if (r.type === 'any') return label;
  const v = (x: string | null) => (r.type === 'date' ? dateText(x) : (x ?? '').replace(/^=/, ''));
  const two = r.operator === 'between' || r.operator === 'notBetween';
  return `${label} ${OP_LABEL[r.operator] ?? r.operator} ${v(r.formula1)}${two ? ` and ${v(r.formula2)}` : ''}`;
}

// ── edits ────────────────────────────────────────────────────────────────────

const DEF_KEYS: (keyof DvDefinition)[] = [
  'range', 'type', 'operator', 'formula1', 'formula2', 'allowBlank', 'inCellDropdown', 'showInputMessage',
  'inputTitle', 'inputMessage', 'showErrorMessage', 'errorStyle', 'errorTitle', 'errorMessage',
];
const sameDef = (a: DvDefinition, b: DvDefinition) => DEF_KEYS.every(k => (a[k] ?? null) === (b[k] ?? null));

const defFields = (r: DvDefinition): Partial<DataValidationEdit> => ({
  range: r.range, type: r.type, operator: r.operator,
  ...(r.formula1 != null ? { formula1: r.formula1 } : {}),
  ...(r.formula2 != null ? { formula2: r.formula2 } : {}),
  allowBlank: r.allowBlank, inCellDropdown: r.inCellDropdown,
  showInputMessage: r.showInputMessage,
  ...(r.inputTitle ? { inputTitle: r.inputTitle } : {}),
  ...(r.inputMessage ? { inputMessage: r.inputMessage } : {}),
  showErrorMessage: r.showErrorMessage, errorStyle: r.errorStyle,
  ...(r.errorTitle ? { errorTitle: r.errorTitle } : {}),
  ...(r.errorMessage ? { errorMessage: r.errorMessage } : {}),
});

/** The server edits that turn `original` into `model` (removes, whole-rule updates, adds). */
export function toDvEdits(original: DvRule[], model: DvRule[]): DataValidationEdit[] {
  const out: DataValidationEdit[] = [];
  const keep = new Set(model.map(r => r.key));
  for (const o of original)
    if (!keep.has(o.key))
      out.push({ sheetIndex: o.sheetIndex, action: 'remove', index: o.origIndex!, range: o.range, oldDescription: describeDv(o) });
  const origByKey = new Map(original.map(r => [r.key, r]));
  for (const r of model) {
    const o = origByKey.get(r.key);
    if (!o) {
      out.push({ sheetIndex: r.sheetIndex, action: 'add', ...defFields(r), description: describeDv(r) });
    } else if (!sameDef(o, r)) {
      out.push({ sheetIndex: r.sheetIndex, action: 'update', index: o.origIndex!, ...defFields(r),
        description: describeDv(r), oldDescription: describeDv(o) });
    }
  }
  return out;
}
